import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import AssetContainer from './AssetContainer';
import { useClientQueryWithSimpleErrorHandling, useInsuranceTypeConfiguration } from 'hooks';
import { useMutation } from 'features/common/security/hooks';
import { INSURANCE_TYPES_QUERY } from '../../../../queries/insuranceDocumentQueries';
import { OFFER_TYPES_QUERY } from 'features/insuranceDocument/edit/general/generalData/queries';
import { CREATE_ASSET } from '../mutations';
import { useDictionaryHandling, useToast, useSimpleError } from 'hooks';
import { useTranslation } from 'react-i18next';
import { CHECK_ACTIVE_OFFERS_BY_CHASSIS_NUMBER } from '../queries';
import { insuranceTypeNames } from 'constants/defaultValues';

const InsuranceAssetContainer = ({
  documentId,
  assetId,
  insuranceTypeId,
  currencyCode,
  offerTypeId,
  startDate,
  financingDocumentId,
  readOnly,
  dirtyInfoRef,
  setDirtyInfoRef
}) => {
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const addToast = useToast();
  const { t } = useTranslation();
  const clientQuery = useClientQueryWithSimpleErrorHandling();
  const { rcaInsuranceTypeConfiguration, cascoInsuranceTypeConfiguration } = useInsuranceTypeConfiguration(
    new Array(insuranceTypeNames.RCA, insuranceTypeNames.CASCO)
  );

  const { getCodeById: getInsuranceTypeCodeById } = useDictionaryHandling(INSURANCE_TYPES_QUERY, 'insuranceTypes');
  const insuranceTypeCode = useMemo(() => getInsuranceTypeCodeById(insuranceTypeId), [getInsuranceTypeCodeById, insuranceTypeId]);

  const { getCodeById: getOfferTypeCodeById } = useDictionaryHandling(OFFER_TYPES_QUERY, 'offerTypes');
  const offerTypeCode = useMemo(() => getOfferTypeCodeById(offerTypeId), [getOfferTypeCodeById, offerTypeId]);

  const showError = useSimpleError();
  const [createAsset] = useMutation(CREATE_ASSET, {
    onError: error => {
      showError(error);
      setSaveDisabled(false);
      setSaving(false);
    }
  });

  const handleCreateAsset = asset => {
    createAsset({
      variables: {
        input: {
          documentId: documentId,
          insuranceTypeCode: insuranceTypeCode,
          asset: asset,
          validateCascoProperties: validateCascoProperties
        }
      }
    });
  };

  const handleCheckActiveOffers = async chassisNumber => {
    const { data } = await clientQuery(CHECK_ACTIVE_OFFERS_BY_CHASSIS_NUMBER, {
      variables: {
        documentId: documentId,
        chassisNumber: chassisNumber,
        insuranceTypeId: insuranceTypeId,
        startDate: startDate
      },
      skip: !documentId || !chassisNumber || !insuranceTypeId
    });
    if (data.checkActiveOffersExistence) {
      addToast(t('Offer.Edit.Asset.ActiveOffers'), 'warning', { toastId: 'checkActiveOffersToast' });
    }
  };

  const validateCascoProperties = useMemo(() => {
    return insuranceTypeCode === cascoInsuranceTypeConfiguration?.PropertyCode && financingDocumentId === null;
  }, [insuranceTypeCode, cascoInsuranceTypeConfiguration?.PropertyCode, financingDocumentId]);

  return (
    <AssetContainer
      documentId={documentId}
      assetId={assetId}
      insuranceTypeId={insuranceTypeId}
      currencyCode={currencyCode}
      validateCascoProperties={validateCascoProperties}
      insuranceTypeCode={insuranceTypeCode}
      offerTypeCode={offerTypeCode}
      rcaInsuranceTypeCode={rcaInsuranceTypeConfiguration?.PropertyCode}
      cascoInsuranceTypeCode={cascoInsuranceTypeConfiguration?.PropertyCode}
      onCreateAsset={handleCreateAsset}
      saveDisabled={saveDisabled}
      setSaveDisabled={setSaveDisabled}
      saving={saving}
      setSaving={setSaving}
      onCheckActiveOffers={handleCheckActiveOffers}
      readOnly={readOnly}
      dirtyInfoRef={dirtyInfoRef}
      setDirtyInfoRef={setDirtyInfoRef}
    />
  );
};

InsuranceAssetContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  assetId: PropTypes.number,
  insuranceTypeId: PropTypes.number.isRequired,
  currencyCode: PropTypes.string.isRequired,
  offerTypeId: PropTypes.number.isRequired,
  startDate: PropTypes.string.isRequired,
  financingDocumentId: PropTypes.number,
  readOnly: PropTypes.func.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};

export default InsuranceAssetContainer;
